import { i18n } from '@goodboydigital/astro';
import { Graphics, ITextStyle, Text } from 'pixi.js';

import { Settings } from '../AppConfig';
import { Shared } from './../App';

export interface LocalisedTextOptions extends Partial<ITextStyle>
{
    maxWidth?: number;
    maxHeight?: number;
    upperCase?: boolean;
    optionalTranslation?: boolean,
    scale?: number
}

export default class LocalisedText extends Text
{
    protected id: string;
    protected maxWidth: number;
    protected maxHeight: number;
    protected upperCase: boolean;
    protected originalSize: unknown;
    protected optionalTranslation: boolean;
    protected debugRect?: Graphics;
    protected params?: Record<string, any>;
    protected defaultScale: number;
    protected textScale: number;

    constructor(id: string, opts: LocalisedTextOptions, noPadding = false)
    {
        opts = opts || {};
        if (!noPadding) opts.padding = 20;
        if (Shared.settings.language === 'ar_AR') opts.letterSpacing = 0;
        super('', opts);

        this.maxWidth = opts.maxWidth || 0;
        this.maxHeight = opts.maxHeight || 0;
        this.defaultScale = opts.scale || 1;
        this.upperCase = opts.upperCase ?? false;
        this.optionalTranslation = opts.optionalTranslation ?? false;
        
        Shared.settings.languageChanged.connect(() => this.refresh());

        this.id = id;
        this.textScale = 1;

        if (this.id) this.setText(id);
    }

    public setText(id: string, params?: Record<string, any>): void
    {
        this.id = id;
        if (!this.id)
        {
            this.text = '';

            return;
        }
        this.params = params;
        this.upperCase = params?.upperCase ?? this.upperCase;
        this.refresh();
    }

    private refresh(): void
    {
        if (!this.id) return;

        if (Shared.settings.language === 'ar_AR') this.style.letterSpacing = 0;

        else if (typeof this.originalSize === 'number')
        {
            this.style.fontSize = this.originalSize;
        }

        let text: string | string[] = '';

        if (typeof this.id === 'string')
        {
            text = this.safeTranslate(this.id, this.params);

            if (!text)
            {
                console.warn('TRANSLATION NOT FOUND', this.id);
                text = this.id;
            }
        }
        else
        {
            // Array

            for (let i = 0; i < (this.id as string).length; i++)
            {
                const id = this.id[i];
                const trans = this.safeTranslate(id, this.params);
                const word = trans || id;

                if (word === '\n')
                {
                    const lastChar = text.substr(text.length - 1);

                    if (lastChar === ' ')
                    {
                        text = text.substr(0, text.length - 1);
                    }
                }
                text += word;
                if (
                    i < (this.id as string).length - 1
                    && word !== '\n'
                    && Shared.settings.language !== 'ar_AR'
                    && Shared.settings.language !== 'no_NO'
                ) text += ' ';
            }
        }
        this.applyText(String(text));
    }

    public applyText(text: string): void
    {
        if (this.upperCase) text = text.toLocaleUpperCase(Shared.settings.language === 'tr_TR' ? 'TR' : 'en-US');
        if (Shared.settings.language === 'ar_AR') text += '\u200F';

        if (this.maxWidth || this.maxHeight)
        {
            this.fitText(text, this.maxWidth, this.maxHeight);
        }
        else
        {
            this.text = text;
        }

        if (Settings.textDebug)
        {
            if (!this.maxWidth && !this.maxHeight) return;
            if (!this.debugRect)
            {
                this.debugRect = new Graphics();
                this.addChild(this.debugRect);
            }
            this.debugRect.clear();
            const width = this.maxWidth || 200;
            const height = this.maxHeight || 50;

            if (this.maxWidth) this.debugRect.beginFill(0xff0000, 0.5);
            else if (this.maxHeight) this.debugRect.beginFill(0x00ff00, 0.5);

            this.debugRect.drawRect(0, 0, width, height);
            this.debugRect.scale.set(1 / this.scale.x);
            this.debugRect.x = -this.debugRect.width / 2;
            this.debugRect.y = -this.debugRect.height / 2;
        }

        this.textScale = this.scale.x;
    }

    protected fitText(string: string, maxWidth?: number, maxHeight?: number) : void
    {

        this.scale.set(this.defaultScale);

        if (string)
        {
            this.text = string;
        }

        var scaleX = maxWidth ? maxWidth / this.width : this.scale.x;
        var scaleY = maxHeight ? maxHeight / this.height : this.scale.y;
        var newScale = Math.min(this.defaultScale, Math.min(scaleX, scaleY));
        this.scale.set(newScale);
        
    }

    protected safeTranslate(id: string, params?: Record<string, any>): string
    {
        try
        {
            return i18n.translate(id, params);
        }
        catch (e)
        {
            if (!this.optionalTranslation)
            {
                console.warn(`[LocalisedText] Missing translation id: ${id}`);
            }

            return id;
        }
    }
}
